import { ElementRef } from '@angular/core';
import * as d3 from 'd3';
import { timeFormatLocale, TimeLocaleDefinition } from 'd3-time-format';

import { UserSettings, I18nService, SettingsUtil } from '@lp-ui-lib';
import { ValueRange, ValueRangeType } from './value-range';
import { Visualization } from './visualization.interface';
import { generateSecureIdentifier } from './visualization-type';

interface LineRawPoint {
  timestamp: Date | string;
  value: number;
  measuredValue: string;
}

/** ---- Locale definitions (periods must be [string, string]) ---- */
const LOCALE_EN: TimeLocaleDefinition = {
  dateTime: '%x, %X',
  date: '%-m/%-d/%Y',
  time: '%-I:%M:%S %p',
  periods: ['AM', 'PM'] as [string, string],
  days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};

const LOCALE_DE: TimeLocaleDefinition = {
  dateTime: '%A, der %e. %B %Y, %X',
  date: '%d.%m.%Y',
  time: '%H:%M:%S',
  periods: ['AM', 'PM'] as [string, string],
  days: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
  shortDays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  months: [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ],
  shortMonths: ['Jan', 'Feb', 'Mrz', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
};

export class LineChart implements Visualization<LineRawPoint, UserSettings> {
  private x_domain: [Date, Date] | null = null;
  private y_domain: [number, number] | null = null;
  private valueRanges: ValueRange[] = [];
  private y_unit = '';
  private booleanDomain = false;
  private measurementId: number | undefined;

  public booleanDomainTrueMessage = '';
  public booleanDomainFalseMessage = '';
  private customRange = false;

  private data: { date: Date; value: number; text: string }[] = [];

  public updateChart: (() => void) | undefined;
  private resetBrush: (() => void) | undefined;
  private brushCallback: ((x_domain: [Date, Date]) => void) | undefined;
  private brushResetCallback: ((x_domain: [Date, Date]) => void) | undefined;

  constructor(
    private chartContainer: ElementRef<HTMLElement>,
    private id: string,
    private i18n: I18nService
  ) {}

  // -------------------------- Visualization API --------------------------

  public render(data: readonly LineRawPoint[], isMobile: boolean, settings: UserSettings): void {
    const incoming = data ?? [];
    if (incoming.length) {
      this.data = incoming.map((t) => {
        const val = Number(t.value.toFixed(2));
        return {
          date: new Date(t.timestamp),
          value: val,
          text: t.measuredValue,
        };
      });
    }
    this.renderGraph(isMobile, settings);
  }

  public SetValueUnit(unit: string): void {
    this.y_unit = unit ?? '';
  }

  public SetXDomain(start: Date, end: Date, custom = false): void {
    // Align right cutoff to next midnight when not custom
    if (!custom && (end.getHours() > 0 || end.getMinutes() > 0)) {
      end = new Date(new Date(end).setDate(end.getDate() + 1));
    }
    if (!custom) start = new Date(start.toDateString());
    if (!custom) end = new Date(end.toDateString());

    this.x_domain = d3.extent([start, end], (d) => d) as [Date, Date];
    this.customRange = custom;
    this.updateChart?.();
  }

  public ResetXDomain(): void {
    this.x_domain = null;
  }

  public SetYDomain(domain: [number, number], booleanDomain = false, measurementID?: number): void {
    if (booleanDomain) {
      this.y_domain = [-0.4, 1.4];
      this.booleanDomain = true;
    } else if (measurementID === 1028) {
      // special DS/RSRP style (reverse domain)
      this.y_domain = [domain[1], domain[0]];
      this.measurementId = measurementID;
    } else {
      // only set if valid
      if (domain[1] > domain[0]) this.y_domain = domain;
    }
    this.updateChart?.();
  }

  public ResetYDomain(): void {
    this.y_domain = null;
  }

  public SetValueRanges(ranges: readonly ValueRange[]): void {
    this.valueRanges = (ranges ?? []) as ValueRange[];
  }

  public OnBrush(callback: (x_domain: [Date, Date]) => void): void {
    this.brushCallback = callback;
  }

  public OnBrushReset(callback: (x_domain: [Date, Date]) => void): void {
    this.brushResetCallback = callback;
  }

  public ResetBrush(): void {
    this.resetBrush?.();
  }

  // -------------------------- Internals --------------------------

  private renderGraph(isMobile: boolean, settings: UserSettings) {
    if (!this.chartContainer) return;

    d3.select(`svg#${this.id}`).remove();

    const max_width = this.chartContainer.nativeElement.clientWidth;
    const max_height = this.chartContainer.nativeElement.clientHeight;
    const isSafari = navigator.userAgent.indexOf('Safari') !== -1;

    const margin = { top: 25, right: 65, bottom: 71, left: 0 };
    const width = max_width - margin.left - margin.right;
    const height = max_height - margin.top - margin.bottom;

    if (height < 0) return;

    // Shared locals across closures
    let svg!: d3.Selection<SVGGElement, unknown, null, undefined>;
    let xAxis!: d3.Selection<SVGGElement, unknown, null, undefined>;
    let yAxis!: d3.Selection<SVGGElement, unknown, null, undefined>;
    let yearAxis!: d3.Selection<SVGGElement, unknown, null, undefined>;
    let clipMask!: d3.Selection<SVGGElement, unknown, null, undefined>;

    let focusLine!: d3.Selection<SVGLineElement, unknown, null, undefined>;
    let focusDate!: d3.Selection<SVGTextElement, unknown, null, undefined>;
    let focusValue!: d3.Selection<SVGTextElement, unknown, null, undefined>;
    let focusValueLine!: d3.Selection<SVGLineElement, unknown, null, undefined>;
    let focusValueBG!: d3.Selection<SVGRectElement, unknown, null, undefined>;
    let circlePointInner!: d3.Selection<SVGCircleElement, unknown, null, undefined>;
    let circlePointOuter!: d3.Selection<SVGCircleElement, unknown, null, undefined>;

    let unique_identifier: string;
    let element: HTMLElement;

    let x!: d3.ScaleTime<number, number>;
    let y!: d3.ScaleLinear<number, number>;
    let x_extent!: [Date, Date];
    let y_extent!: [number, number];

    let bisect!: (data: { date: Date }[], xMs: number) => number;

    let currentData!: { date: Date; value: number; text: string };
    let sx!: number;
    let sy!: number;
    let warning!: boolean;
    let critical!: boolean;
    let noData!: boolean;

    const drawChart = () => {
      init();
      drawAxes();
      drawClipmask();
      drawHorizontalLines();
      if (!noData) drawRanges();
      if (!noData) drawCurve();
      if (!noData) drawFocus();
      initBrushing();
      if (noData) drawNoDataIndicator();
    };

    const init = () => {
      unique_identifier = generateSecureIdentifier();
      element = this.chartContainer.nativeElement;
      noData = (this.data?.length ?? 0) < 2;

      if (noData) {
        this.data = [
          { date: new Date(new Date().setDate(new Date().getDate() - 7)), value: 0, text: '0' },
          { date: new Date(), value: 100, text: '100' },
        ];
      }

      x_extent = d3.extent(this.data, (d) => d.date) as [Date, Date];
      if (!this.x_domain) this.x_domain = [x_extent[0], x_extent[1]];

      const partial = this.data.filter(
        (d) => d.date >= this.x_domain![0] && d.date <= this.x_domain![1]
      );

      if (this.booleanDomain) {
        y_extent = [this.y_domain![0], this.y_domain![1]];
      } else if (this.measurementId === 1028 && this.y_domain) {
        y_extent = [this.y_domain[0], this.y_domain[1]];
      } else {
        const temp: [number, number] = [
          d3.min(partial, (d) => +d.value) ?? 0,
          d3.max(partial, (d) => +d.value) ?? 1,
        ];
        y_extent = this.y_domain
          ? [Math.min(temp[0], this.y_domain[0]), Math.max(temp[1], this.y_domain[1])]
          : temp;

        if (y_extent[0] === y_extent[1]) {
          const e = 0.1 * (y_extent[0] || 1);
          y_extent = [y_extent[0] - e, y_extent[0] + e];
        }
      }

      const buffer = this.booleanDomain ? 0 : (y_extent[1] - y_extent[0]) * 0.1;

      x = d3
        .scaleTime()
        .domain(x_extent)
        .range([20, width - 20]);
      y = d3
        .scaleLinear()
        .domain([y_extent[0] - buffer, y_extent[1] + buffer])
        .range([height, 0]);

      bisect = (data: { date: Date }[], xMs: number) => {
        const right = d3.bisector<{ date: Date }, number>((d) => +d.date).right(data, xMs);
        if (right === 0) return right;
        const left = right - 1;
        if (!data[right]) return left;
        const diff1 = xMs - +data[left].date;
        const diff2 = +data[right].date - xMs;
        return diff1 < diff2 ? left : right;
      };

      currentData = this.data[bisect(this.data, +x_extent[1])];
      sx = x(currentData.date);
      sy = y(currentData.value);

      warning = !!this.valueRanges.find(
        (r) =>
          r.type === ValueRangeType.yellow &&
          r.min <= currentData.value &&
          r.max >= currentData.value
      );
      critical = !!this.valueRanges.find(
        (r) =>
          r.type === ValueRangeType.red && r.min <= currentData.value && r.max >= currentData.value
      );

      initSVG();
    };

    const initSVG = () => {
      const containerSvg = d3
        .select(element)
        .append('svg')
        .attr('id', this.id)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom);

      svg = containerSvg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      const defs = svg.append('defs');
      const filter = defs
        .append('filter')
        .attr('id', 'pill-gradient')
        .attr('height', '1000%')
        .attr('width', '1000%')
        .attr('x', '-500%')
        .attr('y', '-500%');

      filter
        .append('feColorMatrix')
        .attr('type', 'matrix')
        .attr('values', '0 0 0 1 0  0 0 0 1 0  0 0 0 1 0  0 0 0 1 0');

      filter.append('feGaussianBlur').attr('stdDeviation', 2).attr('result', 'coloredBlur');

      const feMerge = filter.append('feMerge');
      feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    };

    const drawClipmask = () => {
      svg
        .append('defs')
        .append('clipPath')
        .attr('id', `line-clip-${this.id}`)
        .append('rect')
        .attr('width', width)
        .attr('height', height + 6)
        .attr('x', 0)
        .attr('y', -5);

      clipMask = svg.append('g').attr('clip-path', `url(#line-clip-${this.id})`);
    };

    const drawAxes = () => {
      svg
        .append('rect')
        .style('fill', '#ffffff')
        .style('cursor', 'pointer')
        .attr('x', -1)
        .attr('y', 0)
        .attr('width', width + 1)
        .attr('height', height);

      if (!noData) {
        xAxis = svg
          .append('g')
          .attr('id', 'x-axis')
          .call(
            d3
              .axisBottom(x)
              .tickSize(height)
              .tickPadding(10)
              .tickFormat(() => '')
          )
          .call((g) =>
            g.selectAll('.tick line').attr('stroke', '#e6e7e7').style('cursor', 'pointer')
          )
          .call((g) => g.select('.domain').remove())
          .call((g) =>
            g
              .selectAll('.tick text')
              .attr('fill', '#afb4b9') // fill (not color)
              .attr('font-size', 12)
              .attr('font-family', 'Roboto')
              .attr('font-weight', '700')
              .attr('line-height', 18 as any)
              .attr('letter-spacing', 0 as any)
              .attr('transform', 'translate(0,-3)')
          );

        yearAxis = svg
          .append('g')
          .attr('id', 'year-axis')
          .call(
            d3
              .axisBottom(x)
              .tickSize(height + 28)
              .ticks(d3.timeYear)
              .tickPadding(15)
              .tickFormat((d: any) => d3.timeFormat('%Y')(d))
          )
          .call((g) => g.selectAll('.tick line').attr('stroke', '#e6e7e7'))
          .call((g) => g.select('.domain').remove())
          .call((g) => g.selectAll('.tick text').attr('fill', '#afb4b9')); // fill
      }

      yAxis = svg
        .append('g')
        .attr('id', 'y-axis')
        .attr('transform', `translate(${width}, 0)`)
        .call(
          this.booleanDomain
            ? d3
                .axisRight(y)
                .tickValues([0, 1])
                .tickFormat((d: any) => d)
            : this.measurementId === 1028
              ? d3
                  .axisRight(y)
                  .tickValues([-90, -100, -110, -120])
                  .tickFormat((d: any) => d)
              : d3
                  .axisRight(y)
                  .ticks(6)
                  .tickFormat((d: any) => (noData ? '' : d + ' ' + this.y_unit))
        )
        .call((g) => g.select('.domain').remove())
        .call((g) => g.selectAll('.tick line').remove())
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('x', 4)
            .attr('fill', '#afb4b9') // fill
            .attr('font-size', 12)
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('text-anchor', 'end')
            .attr('dx', 35)
        );

      // boolean domain special labels/icons
      if (this.booleanDomain) {
        yAxis.selectAll('.tick text').remove();

        yAxis
          .selectAll('.tick:first-child')
          .append('svg:image')
          .attr('xlink:href', 'assets/icons/tilt_untilted.svg')
          .attr('width', 48)
          .attr('height', 48)
          .attr('x', 4)
          .attr('y', -36);

        yAxis
          .selectAll('.tick:last-child')
          .append('svg:image')
          .attr('xlink:href', 'assets/icons/tilt_tilted.svg')
          .attr('width', 48)
          .attr('height', 48)
          .attr('x', 4)
          .attr('y', -32);

        svg
          .append('text')
          .text(this.booleanDomainTrueMessage)
          .attr('y', y(1) - 10)
          .attr('x', width / 2)
          .attr('font-size', 12)
          .attr('font-family', 'Roboto')
          .attr('font-weight', '700')
          .attr('line-height', 18 as any)
          .attr('fill', '#E1000F')
          .attr('letter-spacing', 0 as any)
          .attr('text-anchor', 'middle');

        svg
          .append('text')
          .text(this.booleanDomainFalseMessage)
          .attr('y', y(0) + 20)
          .attr('x', width / 2)
          .attr('font-size', 12)
          .attr('font-family', 'Roboto')
          .attr('font-weight', '700')
          .attr('line-height', 18 as any)
          .attr('letter-spacing', 0 as any)
          .attr('text-anchor', 'middle');
      }

      // measurement 1028 labels
      if (this.measurementId === 1028) {
        yAxis.selectAll('.tick text').remove();

        yAxis
          .selectAll('.tick:first-child')
          .append('text')
          .text('Good -90dBm')
          .attr('font-size', 12)
          .attr('font-family', 'Roboto')
          .attr('font-weight', '700')
          .attr('line-height', 18 as any)
          .attr('fill', '#00aa75')
          .attr('text-anchor', 'start')
          .attr('x', 4)
          .attr('y', -32)
          .call(wrap, 11);

        yAxis
          .selectAll('.tick:nth-child(2)')
          .append('text')
          .text('Fair -100dBm')
          .attr('font-size', 12)
          .attr('font-family', 'Roboto')
          .attr('font-weight', '700')
          .attr('line-height', 18 as any)
          .attr('fill', '#AFB4B9')
          .attr('text-anchor', 'start')
          .attr('x', 4)
          .attr('y', -32)
          .call(wrap, 11);

        yAxis
          .selectAll('.tick:nth-child(3)')
          .append('text')
          .text('Poor -110dBm')
          .attr('font-size', 12)
          .attr('font-family', 'Roboto')
          .attr('font-weight', '700')
          .attr('line-height', 18 as any)
          .attr('fill', '#ECB102')
          .attr('text-anchor', 'start')
          .attr('x', 4)
          .attr('y', -32)
          .call(wrap, 11);

        yAxis
          .selectAll('.tick:last-child')
          .append('text')
          .text('Dead Zone -120dBm')
          .attr('font-size', 12)
          .attr('font-family', 'Roboto')
          .attr('font-weight', '700')
          .attr('line-height', 18 as any)
          .attr('fill', '#E1000F')
          .attr('text-anchor', 'start')
          .attr('x', 4)
          .attr('y', -15)
          .call(wrap, 58);
      }
    };

    const initBrushing = () => {
      const brush = d3
        .brushX()
        .extent([
          [0, -6],
          [sx, height + 12],
        ])
        .on('end', (event: d3.D3BrushEvent<any>) => {
          if (!event.sourceEvent) return;
          const extent = event.selection as [number, number] | null;
          const epsilon = 0.008 * Math.abs(+this.x_domain![1] - +this.x_domain![0]);

          if (
            !extent ||
            !extent[0] ||
            Math.abs(+x.invert(extent[1]) - +x.invert(extent[0])) < epsilon
          ) {
            const px = extent ? extent[0] : (event.sourceEvent as MouseEvent).offsetX;
            // move focus to px
            updateFocusline(x.invert(px));
            updateYAxisValueAt(x.invert(px));
          } else {
            if (minutesBetween(x.invert(extent[0]), x.invert(extent[1])) > 60) {
              if (!unbrushed_domain) unbrushed_domain = this.x_domain!;
              this.SetXDomain(x.invert(extent[0]), x.invert(extent[1]), true);
              this.brushCallback?.(this.x_domain!);
            }
          }
          setTimeout(() => svg.select('.brush').call(brush.move as any, null));
        });

      let unbrushed_domain: [Date, Date] | null = null;
      this.resetBrush = () => {
        if (unbrushed_domain) this.SetXDomain(unbrushed_domain[0], unbrushed_domain[1]);
        unbrushed_domain = null;
        this.brushResetCallback?.(this.x_domain!);

        updateFocusline(this.data[bisect(this.data, +this.x_domain![1])].date, true);
        updateYAxis(this.data[bisect(this.data, +this.x_domain![1])].value);
      };

      svg.on('dblclick', () => this.resetBrush?.());
      clipMask
        .append('g')
        .attr('class', 'brush')
        .call(brush as any);
      clipMask.selectAll('.selection').attr('fill-opacity', 1);
    };

    const drawHorizontalLines = () => {
      clipMask
        .append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', y(0))
        .attr('y2', y(0))
        .attr('stroke', this.booleanDomain ? '#000000' : '#979b9b');

      if (this.booleanDomain) {
        clipMask
          .append('line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', y(1))
          .attr('y2', y(1))
          .attr('stroke', '#E1000F');
      }

      clipMask
        .append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', height)
        .attr('y2', height)
        .attr('stroke', '#e6e7e7');

      svg
        .append('line')
        .attr('x1', 0)
        .attr('x2', max_width)
        .attr('y1', height + 31)
        .attr('y2', height + 31)
        .attr('stroke', '#e6e7e7');
    };

    const drawNoDataIndicator = () => {
      svg.append('text').text('No data').attr('y', 16).attr('x', 36).style('opacity', 0.6);
    };

    const drawRanges = () => {
      for (const r of this.valueRanges) {
        if (r.type === ValueRangeType.red) {
          if (critical) {
            clipMask
              .append('rect')
              .style('fill', '#E1000F')
              .style('cursor', 'pointer')
              .style('opacity', 0.05)
              .attr('x', 0)
              .attr('y', y(r.max))
              .attr('width', width)
              .attr('height', y(r.min) - y(r.max));
          }
          if (r.min > y_extent[0]) {
            clipMask
              .append('line')
              .attr('x1', 0)
              .attr('x2', width)
              .attr('y1', y(r.min))
              .attr('y2', y(r.min))
              .attr('stroke', '#E1000F')
              .style('cursor', 'pointer');
          }
          if (r.max < y_extent[1]) {
            clipMask
              .append('line')
              .attr('x1', 0)
              .attr('x2', width)
              .attr('y1', y(r.max))
              .attr('y2', y(r.max))
              .attr('stroke', '#E1000F')
              .style('cursor', 'pointer');
          }
        }
      }

      // Gradient by critical ranges
      clipMask
        .append('linearGradient')
        .attr('id', 'line-gradient' + unique_identifier)
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', 0)
        .attr('y1', y(y_extent[0]))
        .attr('x2', 0)
        .attr('y2', y(y_extent[1]))
        .selectAll('stop')
        .data(
          generateCriticalGradient(this.valueRanges, y_extent, critical, this.booleanDomain) as any
        )
        .enter()
        .append('stop')
        .attr('offset', (d: any) => d.offset)
        .attr('stop-color', (d: any) => d.color);
    };

    const drawCurve = () => {
      clipMask
        .append('path')
        .attr('id', 'datapath')
        .datum(this.data)
        .attr('class', 'curve')
        .attr('fill', 'none')
        .attr('stroke', 'url(#line-gradient' + unique_identifier + ')')
        .attr('stroke-width', 3)
        .style('cursor', 'pointer')
        .attr(
          'd',
          (isSafari
            ? d3
                .line<any>()
                .x((d) => x(d.date))
                .y((d) => y(d.value))
            : d3
                .line<any>()
                .x((d) => x(d.date))
                .y((d) => y(d.value))
                .curve(d3.curveMonotoneX)) as any
        );
    };

    const drawFocus = () => {
      if (this.data.length < 2) return;

      const buffer = this.booleanDomain ? 0 : (y_extent[1] - y_extent[0]) * 0.1;

      focusLine = svg
        .append('line')
        .attr('x1', sx)
        .attr(
          'y1',
          y((this.y_domain ? this.y_domain[1] : (d3.max(this.data, (d) => +d.value) ?? 0)) + buffer)
        )
        .attr('x2', sx)
        .attr(
          'y2',
          y((this.y_domain ? this.y_domain[0] : (d3.min(this.data, (d) => +d.value) ?? 0)) - buffer)
        )
        .attr('stroke', 'black')
        .style('cursor', 'pointer');

      const df =
        !!SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID) &&
        SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID)?.formatString ===
          'MM/dd/yyyy'
          ? '%m/%d'
          : '%e-%m';

      focusDate = svg
        .append('text')
        .text(d3.timeFormat(df)(this.data[this.data.length - 1].date))
        .attr('font-size', 12)
        .attr('font-family', 'Roboto')
        .attr('font-weight', '700')
        .attr('line-height', 18 as any)
        .attr('letter-spacing', 0 as any)
        .attr('dy', -4)
        .attr('class', 'brushText2Time')
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'middle')
        .attr('y', height + (isSafari ? 16 : 21))
        .attr('x', sx);

      focusValueBG = svg
        .append('rect')
        .style('fill', 'white')
        .attr('x', width)
        .attr('y', sy)
        .attr('width', 46)
        .attr('height', 12)
        .attr('opacity', 0)
        .style('filter', 'url(#pill-gradient)')
        .attr('rx', '3')
        .attr('ry', '3');

      focusValue = svg
        .append('text')
        .text(this.data[this.data.length - 1].value + this.y_unit)
        .attr('x', width)
        .attr('y', sy)
        .attr('fill', critical ? '#E1000F' : warning ? '#FFCC33' : '#00AA75')
        .attr('font-size', 12)
        .attr('font-family', 'Roboto')
        .attr('font-weight', '700')
        .attr('line-height', 18 as any)
        .attr('letter-spacing', 0 as any)
        .attr('text-anchor', 'end')
        .attr('dx', 40)
        .attr('dy', 4);

      focusValueLine = svg
        .append('line')
        .attr('x1', sx)
        .attr('x2', width - 5)
        .attr('y1', sy)
        .attr('y2', sy)
        .attr('stroke', critical ? '#E1000F' : warning ? '#FFCC33' : '#00AA75');

      circlePointInner = svg
        .append('circle')
        .attr('class', 'inner')
        .attr('fill', critical ? '#E1000F' : warning ? '#FFCC33' : '#00AA75')
        .attr('cx', sx)
        .attr('cy', sy)
        .attr('r', 8);

      circlePointOuter = svg
        .append('circle')
        .attr('class', 'outer')
        .attr('cx', sx)
        .attr('cy', sy)
        .attr('fill', 'transparent')
        .attr('stroke', critical ? '#E1000F' : warning ? '#FFCC33' : '#00AA75')
        .attr('r', 12);
    };

    this.updateChart = () => {
      if (!noData) updateXAxis();
      if (!noData) updateYAxis();
      if (!noData) updateCurve();
      if (!noData) updateFocusPoint();
      mouseout();
    };

    const updateCurve = () => {
      clipMask
        .select('.curve')
        .transition()
        .duration(1000)
        .attr(
          'd',
          (isSafari
            ? d3
                .line<any>()
                .x((d) => x(d.date))
                .y((d) => y(d.value))
            : d3
                .line<any>()
                .x((d) => x(d.date))
                .y((d) => y(d.value))
                .curve(d3.curveMonotoneX)) as any
        );
    };

    const mouseout = () => {
      if (noData) return;

      const idx = bisect(this.data, +this.x_domain![1]);
      const d = this.data[idx];

      updateFocusline(d.date, true);
      updateYAxis(d.value);

      focusValue.attr('opacity', this.booleanDomain ? 0 : 1);
      focusValueBG.attr('opacity', 0);
      focusValueLine.attr('opacity', 0);
    };

    const updateFocusline = (x0: Date, reset = false) => {
      if (noData || !this.data?.length) return;

      const i = bisect(this.data, +x0);
      const sel = this.data[i];
      if (!sel) return;

      let _sx = x(sel.date);
      _sx = Math.max(0, Math.min(_sx, width));

      const nextWarning = !!this.valueRanges.find(
        (r) => r.type === ValueRangeType.yellow && r.min <= sel.value && r.max >= sel.value
      );

      const nextCritical = this.booleanDomain
        ? sel.value > 0
        : !!this.valueRanges.find(
            (r) => r.type === ValueRangeType.red && r.min <= sel.value && r.max >= sel.value
          );

      const _sy = y(sel.value);

      focusLine
        .transition()
        .duration(reset ? 1000 : 500)
        .attr('x1', _sx)
        .attr('x2', _sx);

      circlePointInner
        .transition()
        .duration(reset ? 1000 : 500)
        .attr('cx', _sx)
        .attr('cy', _sy)
        .attr('fill', nextCritical ? '#E1000F' : nextWarning ? '#FFCC33' : '#00AA75');

      circlePointOuter
        .transition()
        .duration(reset ? 1000 : 500)
        .attr('cx', _sx)
        .attr('cy', _sy)
        .attr('stroke', nextCritical ? '#E1000F' : nextWarning ? '#FFCC33' : '#00AA75');

      focusValue
        .text(sel.value + this.y_unit)
        .transition()
        .duration(reset ? 1000 : 500)
        .attr('y', _sy)
        .attr('fill', nextCritical ? '#E1000F' : nextWarning ? '#FFCC33' : '#00AA75')
        .attr('opacity', this.booleanDomain ? 0 : 1);

      focusValueLine
        .transition()
        .duration(reset ? 1000 : 500)
        .attr('x1', _sx)
        .attr('y1', _sy)
        .attr('y2', _sy)
        .attr('stroke', nextCritical ? '#E1000F' : nextWarning ? '#FFCC33' : '#00AA75')
        .attr('opacity', this.booleanDomain ? 0 : 1);
    };

    const updateYAxisValueAt = (x0: Date) => {
      if (noData) return;
      const i = bisect(this.data, +x0);
      const v = this.data[i]?.value;
      if (v != null) updateYAxis(v);
    };

    const updateXAxis = () => {
      if (this.x_domain) x.domain(this.x_domain);

      const tickCall = generateTicks(this.x_domain![0], this.x_domain![1]);

      xAxis
        .transition()
        .duration(1000)
        .call(tickCall)
        .call((g) => g.selectAll('.tick line').attr('stroke', '#e6e7e7'))
        .call((g) => g.select('.domain').remove())
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('fill', '#afb4b9') // fill
            .attr('font-size', isMobile ? '12px' : '14px')
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('transform', 'translate(0,-3)')
        );

      const yearTransitionInRange =
        d3.timeYear.range(this.x_domain![0], this.x_domain![1]).length > 0;

      yearAxis
        .transition()
        .duration(1000)
        .call(
          d3
            .axisBottom(x)
            .tickSize(height + (isSafari ? 30 : 28))
            .tickValues(
              yearTransitionInRange
                ? d3.timeYear.range(this.x_domain![0], this.x_domain![1])
                : [new Date((+this.x_domain![0] + +this.x_domain![1]) / 2)]
            )
            .tickPadding(15)
            .tickFormat((d: any) => (noData ? '' : d3.timeFormat('%Y')(d)))
        )
        .call((g) => g.selectAll('.tick line').attr('stroke', 'transparent'))
        .call((g) => g.select('.domain').remove())
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('fill', '#afb4b9') // fill
            .attr('font-size', 12)
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('dx', yearTransitionInRange ? 0 : (margin.right - margin.left) / 2)
        )
        .call((g) => g.selectAll('.tick:last-child text').attr('fill', 'black')); // fill
    };

    const updateFocusPoint = () => {
      if (this.data.length < 2) return;

      currentData = this.data[bisect(this.data, +this.x_domain![1])];
      critical = this.booleanDomain
        ? currentData.value > 0
        : !!this.valueRanges.find(
            (r) =>
              r.type === ValueRangeType.red &&
              r.min <= currentData.value &&
              r.max >= currentData.value
          );

      sy = y(currentData.value);

      circlePointInner
        .transition()
        .duration(1000)
        .attr('cy', sy)
        .attr('fill', critical ? '#E1000F' : warning ? '#FFCC33' : '#00AA75');

      circlePointOuter
        .transition()
        .duration(1000)
        .attr('cy', sy)
        .attr('stroke', critical ? '#E1000F' : warning ? '#FFCC33' : '#00AA75');

      const df =
        !!SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID) &&
        SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID)?.formatString ===
          'MM/dd/yyyy'
          ? '%m/%d'
          : '%e-%m';

      focusDate
        .text(d3.timeFormat(df)(this.data[bisect(this.data, +this.x_domain![1])].date))
        .attr('font-size', isMobile ? '12px' : '14px')
        .attr('font-family', 'Roboto')
        .attr('font-weight', '700')
        .attr('line-height', 18 as any)
        .attr('letter-spacing', 0 as any)
        .attr('transform', 'translate(0,2)');
    };

    const updateYAxis = (s: number | null = null) => {
      if (this.booleanDomain || this.measurementId === 1028) return;

      if (s == null) {
        s = this.data[bisect(this.data, +this.x_domain![1])].value;
      }

      const partial = this.data.filter(
        (d) => d.date > this.x_domain![0] && d.date < this.x_domain![1]
      );
      if (partial.length < 2) return;

      const temp: [number, number] = [
        d3.min(partial, (d) => +d.value) ?? 0,
        d3.max(partial, (d) => +d.value) ?? 1,
      ];

      y_extent = this.y_domain
        ? [Math.min(temp[0], this.y_domain[0]), Math.max(temp[1], this.y_domain[1])]
        : temp;

      const buffer = (y_extent[1] - y_extent[0]) * 0.1;
      if (y_extent[0] < y_extent[1]) {
        y.domain([y_extent[0] - buffer, y_extent[1] + buffer]);
      }

      let last_d: number | null = null;
      let delta: number | null = null;
      const epsilon = 0.3;

      yAxis
        .transition()
        .duration(1000)
        .call(
          d3
            .axisRight(y)
            .ticks(6)
            .tickFormat((d: any) => {
              if (noData) return '';
              if (last_d != null) delta = d - last_d;
              last_d = d;
              return d + ' ' + this.y_unit;
            })
        )
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('x', 4)
            .attr('fill', '#afb4b9') // fill
            .attr('opacity', (d: any) => {
              // hide tick closest to focus value
              return delta && Math.abs((s as number) - d) < delta * epsilon ? 0 : 1;
            })
            .attr('font-size', 12)
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('text-anchor', 'end')
            .attr('dx', 35)
        );

      yAxis.call((g) => g.selectAll('.tick line').remove());
    };

    const generateTicks = (start: Date, end: Date) => {
      const small_screen = width < 450;
      const timespan_days = daysBetween(start, end);
      const timespan_hours = hoursBetween(start, end);

      const abbr =
        SettingsUtil.getLanguage(settings.languages, settings.languageID)?.abbreviation ?? 'en';
      const locale = timeFormatLocale(abbr === 'de' ? LOCALE_DE : LOCALE_EN);

      // Use TimeInterval with safe every()
      let ticks: d3.TimeInterval = everyOr(
        d3.timeDay as d3.CountableTimeInterval,
        small_screen ? 2 : 1
      );

      let tickFormat: (d: Date) => string = (d) =>
        d3.timeFormat(
          !!SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID) &&
            SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID)
              ?.formatString === 'MM/dd/yyyy'
            ? '%m/%d'
            : '%e-%m'
        )(d);

      const tickValuesRangeEnd = minDate(new Date(this.data[this.data.length - 1].date), end);
      const tickValuesRangeStart = start;

      if (timespan_days > 15) {
        ticks = everyOr(d3.timeWeek as d3.CountableTimeInterval, 1);
        tickFormat = (d) => (abbr === 'de' ? 'KW' : 'CW') + d3.timeFormat('%U')(d);
      }
      if (timespan_days > 45 || (small_screen && timespan_days > 25)) {
        ticks = everyOr(d3.timeWeek as d3.CountableTimeInterval, 2);
      }
      if (timespan_days > 90 || (small_screen && timespan_days > 60)) {
        ticks = everyOr(d3.timeMonth as d3.CountableTimeInterval, 1);

        if (!small_screen) {
          const tempTicks = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);
          const widthPerTick = width / Math.max(tempTicks.length, 1);
          if (widthPerTick < 35) ticks = everyOr(d3.timeMonth as d3.CountableTimeInterval, 4);
          else if (widthPerTick < 75) ticks = everyOr(d3.timeMonth as d3.CountableTimeInterval, 2);
        }
        tickFormat = (d) => locale.format(small_screen ? '%b' : '%B')(d);
      }

      const end_sx = x(this.data[this.data.length - 1].date);
      let tickValues = ticks
        .range(tickValuesRangeStart, tickValuesRangeEnd)
        .filter(
          (d) =>
            x(d) >= (timespan_days > 90 || (small_screen && timespan_days > 60) ? 30 : 20) &&
            x(d) <= end_sx - (small_screen ? 30 : 60)
        );

      if (noData) tickFormat = () => '';

      let tickCall = d3
        .axisBottom(x)
        .tickSize(height)
        .tickValues(tickValues)
        .tickPadding(12)
        .tickFormat(tickFormat as any);

      if (this.customRange) {
        if (timespan_days <= 15 && timespan_days > 7) {
          ticks = everyOr(
            d3.timeDay as d3.CountableTimeInterval,
            small_screen && timespan_days >= 6 ? 3 : 2
          );
          tickValues = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);
        } else if (timespan_hours < 3) {
          ticks = everyOr(
            d3.timeMinute as d3.CountableTimeInterval,
            small_screen ? (timespan_hours < 1 ? 60 : 30) : timespan_hours < 1 ? 30 : 15
          );
          tickValues = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);
        } else if (timespan_hours <= 24) {
          ticks = everyOr(
            d3.timeHour as d3.CountableTimeInterval,
            small_screen
              ? timespan_hours < 6
                ? 2
                : timespan_hours < 12
                  ? 3
                  : 6
              : timespan_hours < 6
                ? 1
                : timespan_hours < 12
                  ? 2
                  : 3
          );
          tickValues = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);

          if (tickValues.length < 5 && timespan_hours < 6 && !small_screen) {
            tickValues = (tickValues as Date[])
              .flatMap((d) => [
                new Date(new Date(d).setMinutes(d.getMinutes() - 30)),
                d,
                new Date(new Date(d).setMinutes(d.getMinutes() + 30)),
              ])
              .filter((d) => d < tickValuesRangeEnd)
              .filter(
                (d) =>
                  x(d) >= (timespan_days > 90 || (small_screen && timespan_days > 60) ? 30 : 20) &&
                  x(d) <= end_sx - (small_screen ? 30 : 60)
              );
          }
        } else if (timespan_days <= 7) {
          ticks = everyOr(
            d3.timeDay as d3.CountableTimeInterval,
            small_screen && timespan_days >= 6 ? 2 : 1
          );
          tickValues = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);

          if (tickValues.length < 5 && !small_screen) {
            tickValues = (tickValues as Date[])
              .flatMap((d) => [
                new Date(new Date(d).setHours(d.getHours() - 12)),
                d,
                new Date(new Date(d).setHours(d.getHours() + 12)),
              ])
              .filter((d) => d < tickValuesRangeEnd)
              .filter(
                (d) =>
                  x(d) >= (timespan_days > 90 || (small_screen && timespan_days > 60) ? 30 : 20) &&
                  x(d) <= end_sx - (small_screen ? 30 : 60)
              );
          }
        }

        const _locale = timeFormatLocale(abbr === 'de' ? LOCALE_DE : LOCALE_EN);

        tickCall = d3
          .axisBottom(x)
          .tickSize(height)
          .tickValues(tickValues)
          .tickPadding(12)
          .tickFormat(
            timespan_days > 90 || (small_screen && timespan_days > 60)
              ? (((d: Date) => _locale.format(small_screen ? '%b' : '%B')(d)) as any)
              : timespan_days > 15
                ? (((d: Date) => (abbr === 'de' ? 'KW' : 'CW') + d3.timeFormat('%U')(d)) as any)
                : timespan_days <= 1
                  ? (((d: Date) => d3.timeFormat('%H:%M')(d)) as any)
                  : timespan_days < 7
                    ? (((d: Date) => {
                        if (d.getHours() > 0 || d.getMinutes() > 0)
                          return _locale.format('%H:%M')(d);
                        return _locale.format(
                          !!SettingsUtil.getDateFormat(
                            settings.dateFormats,
                            settings.dateFormatID
                          ) &&
                            SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID)
                              ?.formatString === 'MM/dd/yyyy'
                            ? '%m/%d'
                            : '%e-%m'
                        )(d);
                      }) as any)
                    : (tickFormat as any)
          );
      }

      return tickCall;
    };

    drawChart();
    setTimeout(() => this.updateChart?.(), 0);
  }
}

export function wrap(
  selection: d3.Selection<SVGTextElement, unknown, any, any>,
  width: number
): void {
  selection.each(function (this: SVGTextElement) {
    const sel = d3.select<SVGTextElement, unknown>(this);
    const words = (sel.text() || '').split(/\s+/).reverse();
    let word: string | undefined;
    let line: string[] = [];
    let lineNumber = 1;
    const lineHeight = 15;

    const y = Number(sel.attr('y') ?? 0);
    const dy = Number(sel.attr('dy') ?? 0);

    let tspan = sel
      .text(null)
      .attr('text-anchor', 'start')
      .append('tspan')
      .attr('x', 0)
      .attr('y', y)
      .attr('dy', `${dy - 2}`);

    while ((word = words.pop())) {
      line.push(word);
      tspan.text(line.join(' ')).attr('text-anchor', 'start');

      const node = tspan.node() as SVGTSpanElement | null;
      if (node && node.getComputedTextLength() > width) {
        line.pop();
        tspan.text(line.join(' '));

        line = [word];
        tspan = sel
          .append('tspan')
          .attr('text-anchor', 'start')
          .attr('x', 0)
          .attr('y', y)
          .attr('dy', `${lineNumber++ * lineHeight}`)
          .text(word);
      }
    }
  });
}
/** Countable every() safe fallback to a TimeInterval */
function everyOr(interval: d3.CountableTimeInterval, step: number): d3.TimeInterval {
  return interval.every(step) ?? interval;
}

// -------------------------- Utilities --------------------------
function treatAsUTC(date: Date | string): number {
  const result = new Date(date);
  result.setMinutes(result.getMinutes() - result.getTimezoneOffset());
  return +result;
}
function daysBetween(startDate: Date, endDate: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return (treatAsUTC(endDate) - treatAsUTC(startDate)) / msPerDay;
}
function hoursBetween(startDate: Date, endDate: Date): number {
  const msPerHour = 60 * 60 * 1000;
  return (treatAsUTC(endDate) - treatAsUTC(startDate)) / msPerHour;
}
function minutesBetween(startDate: Date, endDate: Date): number {
  const msPerMin = 60 * 1000;
  return (treatAsUTC(endDate) - treatAsUTC(startDate)) / msPerMin;
}
function minDate(a: Date, b: Date) {
  return a < b ? a : b;
}

function generateCriticalGradient(
  ranges: ValueRange[],
  y_extent: [number, number],
  critical: boolean,
  booleanDomain: boolean
) {
  if (booleanDomain) {
    return [
      { offset: '20%', color: 'black' },
      { offset: '80%', color: '#E1000F' },
    ];
  }
  if (!critical) {
    return [
      { offset: '-100%', color: 'black' },
      { offset: '100%', color: 'black' },
    ];
  }
  let above_crit_offsets: { offset: string; color: string }[] = [];
  let below_crit_offsets: { offset: string; color: string }[] = [];

  const above_crit_range = ranges.find(
    (red) =>
      red.type === ValueRangeType.red &&
      !!ranges.find((green) => green.type === ValueRangeType.green && green.max <= red.min)
  );

  const below_crit_range = ranges.find(
    (red) =>
      red.type === ValueRangeType.red &&
      !!ranges.find((green) => green.type === ValueRangeType.green && green.min >= red.max)
  );

  const full_range = y_extent[1] - y_extent[0];
  const offset = full_range * 0.05;

  if (above_crit_range) {
    const gradient_start = above_crit_range.min - offset - y_extent[0];
    const gradient_end = above_crit_range.min + offset - y_extent[0];

    above_crit_offsets = [
      { offset: ((gradient_start / full_range) * 100).toFixed(0) + '%', color: 'black' },
      { offset: ((gradient_end / full_range) * 100).toFixed(0) + '%', color: '#E1000F' },
    ];
  }

  if (below_crit_range) {
    const gradient_start = below_crit_range.max - offset - y_extent[0];
    const gradient_end = below_crit_range.max + offset - y_extent[0];

    below_crit_offsets = [
      { offset: ((gradient_start / full_range) * 100).toFixed(0) + '%', color: '#E1000F' },
      { offset: ((gradient_end / full_range) * 100).toFixed(0) + '%', color: 'black' },
    ];
  }

  return [
    { offset: '-100%', color: below_crit_range ? '#E1000F' : 'black' },
    ...below_crit_offsets,
    ...above_crit_offsets,
    { offset: '100%', color: above_crit_range ? '#E1000F' : 'black' },
  ];
}
